import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  useState,
} from "react";
import type { ReactNode } from "react";
import { ChevronRightIcon, MoreHorizIcon } from "../lib/icons";
import { cn } from "../lib/cn";

/**
 * Breadcrumbs — nav > ol > li trail with a chevron separator.
 *
 *   <Breadcrumbs>
 *     <BreadcrumbsItem><Link to="/sites">Sites</Link></BreadcrumbsItem>
 *     <BreadcrumbsItem aria-current="page">Plot 12</BreadcrumbsItem>
 *   </Breadcrumbs>
 *
 * The last item is marked aria-current="page" automatically (unless it
 * already carries an aria-current), and items style themselves off that
 * attribute.
 *
 * Overflow (MUI Breadcrumbs semantics): with `maxItems`, a longer trail
 * renders `itemsBeforeCollapse` items, one collapsed control, then
 * `itemsAfterCollapse` items. The built-in control expands the trail on
 * click; `renderCollapsed` replaces it (e.g. the portal's "…" menu button).
 */

export interface BreadcrumbsItemProps
  extends React.LiHTMLAttributes<HTMLLIElement> {
  /** Ellipsize this step when the trail runs out of width (min-w-0 + truncate). Switches the ol to a single line. */
  truncate?: boolean;
}

/** Separator between two items — decorative, hidden from a11y tree. */
function BreadcrumbsSeparator() {
  return (
    <li aria-hidden="true" className="flex items-center">
      <ChevronRightIcon className="size-3.5 shrink-0 text-content-disabled" />
    </li>
  );
}

/** Mark the trail's last item as the current page unless it already is. */
function withCurrentPage(child: React.ReactNode, isLast: boolean) {
  if (!isLast || !isValidElement<BreadcrumbsItemProps>(child)) {
    return child;
  }
  if (child.props["aria-current"] !== undefined) {
    return child;
  }
  return cloneElement(child, { "aria-current": "page" });
}

/** Built-in collapsed control — "…" button that expands the full trail. */
function BreadcrumbsCollapsedButton({ onExpand }: { onExpand: () => void }) {
  return (
    <button
      type="button"
      onClick={onExpand}
      aria-label="Show full path"
      data-testid="breadcrumb-collapse-button"
      className="flex size-5 items-center justify-center rounded-md text-content-secondary transition-colors duration-150 outline-none hover:bg-surface-raised hover:text-content-primary focus-visible:ring-2 focus-visible:ring-border-focused"
    >
      <MoreHorizIcon className="size-3.5" aria-hidden="true" />
    </button>
  );
}

/** The collapsed trail entry — wraps the custom or built-in control. */
function BreadcrumbsCollapsed({
  renderCollapsed,
  onExpand,
}: {
  renderCollapsed?: (expand: () => void) => ReactNode;
  onExpand: () => void;
}) {
  return (
    <li className="flex shrink-0 items-center">
      {renderCollapsed ? renderCollapsed(onExpand) : <BreadcrumbsCollapsedButton onExpand={onExpand} />}
    </li>
  );
}

export interface BreadcrumbsProps
  extends React.HTMLAttributes<HTMLElement> {
  /** Collapse the middle of the trail when there are more items than this. */
  maxItems?: number;
  /** Items kept before the collapsed control when maxItems overflows (default 1). */
  itemsBeforeCollapse?: number;
  /** Items kept after the collapsed control when maxItems overflows (default 1). */
  itemsAfterCollapse?: number;
  /** Replaces the built-in "…" expand button — receives expand() to reveal the full trail. */
  renderCollapsed?: (expand: () => void) => ReactNode;
}

export function Breadcrumbs({
  className,
  children,
  maxItems,
  itemsBeforeCollapse = 1,
  itemsAfterCollapse = 1,
  renderCollapsed,
  ...props
}: BreadcrumbsProps) {
  const [expanded, setExpanded] = useState(false);
  const items = Children.toArray(children);
  const last = items.length - 1;

  // Any truncating step pins the trail to one shrinking line.
  const truncating = items.some((item) => {
    return isValidElement<BreadcrumbsItemProps>(item) && Boolean(item.props.truncate);
  });

  const collapsed =
    maxItems !== undefined && items.length > maxItems && !expanded;
  const afterCount = collapsed
    ? Math.max(0, Math.min(itemsAfterCollapse, last - itemsBeforeCollapse))
    : 0;
  const visible = collapsed
    ? [
        ...items.slice(0, itemsBeforeCollapse),
        <BreadcrumbsCollapsed
          key="breadcrumbs-collapsed"
          renderCollapsed={renderCollapsed}
          onExpand={() => {
            setExpanded(true);
          }}
        />,
        ...items.slice(items.length - afterCount),
      ]
    : items;

  const trail = visible.flatMap((item, index) => {
    if (index === 0) {
      return [withCurrentPage(item, index === visible.length - 1)];
    }
    return [
      <BreadcrumbsSeparator key={`separator-${index}`} />,
      withCurrentPage(item, index === visible.length - 1),
    ];
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className={cn("min-w-0", truncating && "overflow-hidden", className)}
      {...props}
    >
      <ol
        className={cn(
          "flex items-center gap-1.5",
          truncating ? "min-w-0 flex-nowrap" : "flex-wrap",
        )}
      >
        {trail}
      </ol>
    </nav>
  );
}
Breadcrumbs.displayName = "Breadcrumbs";

/** Trail step — links inherit; the aria-current step lifts to primary. */
export const BreadcrumbsItem = forwardRef<HTMLLIElement, BreadcrumbsItemProps>(
  ({ className, truncate = false, ...props }, ref) => {
    return (
      <li
        ref={ref}
        className={cn(
          "type-body-md text-content-secondary",
          "aria-[current=page]:font-medium aria-[current=page]:text-content-primary",
          truncate ? "min-w-0 truncate" : "flex items-center",
          className,
        )}
        {...props}
      />
    );
  },
);
BreadcrumbsItem.displayName = "BreadcrumbsItem";
